/**
 * CSS-строка для НЕ-пузырной части ленты (STEP8 эт.2, доборка).
 *
 * Почему отдельно от bubbleCss.ts: договорённость с Момо — форма пузыря,
 * хвостик, время и подсветка правятся ТОЛЬКО в bubbleCss.ts (по его
 * классам он ищет пузыри в собранном бандле). Здесь живут остальные
 * элементы ленты, которым inline-стиля не хватает: :hover у пунктов
 * контекстного меню и ссылок inline не выразить в принципе.
 *
 * Цвета — только из theme.ts, литеральных hex здесь быть не должно.
 */
import { color, font, radius } from "./theme";

export const timelineCss = `
/* ССЫЛКА в теле сообщения. Акцентный цвет + подчёркивание при наведении:
   без :hover ссылка читалась бы просто цветным текстом, а inline-стиль
   наведение не умеет. */
.alg-msg-link {
  color: ${color.accent};
  text-decoration: none;
  /* Длинный URL без пробелов обязан ломаться, а не распирать пузырь:
     break-all здесь уместен, потому что у ссылки перенос в любом месте
     читается лучше, чем горизонтальный скролл или вылезший за край текст. */
  word-break: break-all;
}
.alg-msg-link:hover { text-decoration: underline; }

/* КОНТЕКСТНОЕ МЕНЮ сообщения (правый клик) — приведено к палитре.
   Наведение — нейтральное color.hover, как в списке комнат: акцент
   оставлен действиям, а не подсветке строки меню. */
.alg-menu {
  background: ${color.bg};
  border: 1px solid ${color.border};
  border-radius: ${radius.md}px;
  /* Тень — через color.backdrop: литеральный rgba здесь был бы вторым
     источником того же тёмного тона мимо theme.ts. */
  box-shadow: 0 4px 16px ${color.backdrop};
}
.alg-menu-item {
  font: inherit;
  font-size: ${font.small}px;
  text-align: left;
  padding: 7px 10px;
  border: none;
  background: transparent;
  color: ${color.text};
  cursor: pointer;
  border-radius: ${radius.sm}px;
  white-space: nowrap;
  width: 100%;
  box-sizing: border-box;
}
.alg-menu-item:hover:not(:disabled) { background: ${color.hover}; }
.alg-menu-item:disabled { opacity: 0.4; cursor: default; }
/* Разрушительный пункт («Удалить»): цветом danger всегда, а при наведении
   мягким фоном dangerBg — чтобы опасное действие отличалось не только
   буквами, но и плашкой. */
.alg-menu-item.danger { color: ${color.danger}; }
.alg-menu-item.danger:hover:not(:disabled) { background: ${color.dangerBg}; }

/* СПИСКИ в теле сообщения.

   История: 2026-09-16 «пропали маркеры нумерованных списков» — renderFormatted
   стал сохранять <ol>/<ul>/<li> настоящими элементами. 2026-09-18 п.4 (Антон):
   «рендерятся не так, как должны, эталон — Element Web». Разница с Element,
   которую закрывает этот блок:
     1. отступ был 22px ФИКСИРОВАННЫЙ — при другом размере шрифта маркер
        отрывался от текста; Element отмеряет отступ в em от текста;
     2. вложенный список получал тот же отступ, что и внешний, — ступеньки
        вложенности читались плохо;
     3. у вложенных <ul> маркер оставался disc на всех уровнях; Element (как
        и браузерный дефолт) чередует disc → circle → square;
     4. между пунктами стоял двойной воздух (margin у <li> сверху И снизу
        плюс margin абзаца внутри) — список выглядел разреженнее, чем
        соседний текст.

   Остальное — обязательная подстройка под пузырь:
   - white-space: normal — тело сообщения живёт в s.msgBody с pre-wrap,
     и у HTML-клиентов (Element, markdown-it) между </li> и <li> стоят
     переводы строк: с унаследованным pre-wrap каждый пункт отделялся бы
     пустой строкой. Внутри списка переносы из разметки не значимы
     (мусорные whitespace-узлы вдобавок отбрасывает renderFormatted);
   - padding-inline-start (а не padding-left) — логическое свойство, RTL
     не ломается;
   - list-style-position: outside — маркер в поле отступа, вторая строка
     пункта выравнивается по тексту, а не под номер. Как в Element.
   Сами номера и маркеры считает браузер; вложенность штатная. text-align
   не трогаем — наследуется от тела сообщения, как у остального текста. */
.alg-msg-list {
  margin: 4px 0;
  /* 1.5em ≈ 21px при 14px тексте: место под «10.» без обрезки, и отступ
     растёт вместе со шрифтом. То же соотношение, что у списков Element. */
  padding-inline-start: 1.5em;
  /* НЕ добавлять padding-left: 0 — в LTR это то же физическое свойство, что
     padding-inline-start; объявление ниже обнуляло отступ, и номера вылезали
     за левый край баллона (Антон 2026-09-21 08:22 UTC). */
  white-space: normal;
  list-style-position: outside;
}
.alg-msg-ordered-list { list-style-type: decimal; }
.alg-msg-bullet-list { list-style-type: disc; }
/* Уровни вложенности маркируются по-разному — иначе три уровня выглядят
   одинаковыми точками и структура теряется. */
.alg-msg-bullet-list .alg-msg-bullet-list { list-style-type: circle; }
.alg-msg-bullet-list .alg-msg-bullet-list .alg-msg-bullet-list { list-style-type: square; }
/* Вложенный список примыкает к своему пункту: воздух сверху/снизу здесь
   лишний, ступеньку задаёт отступ, а не пустая строка. */
.alg-msg-list .alg-msg-list { margin: 2px 0 0; }
/* Воздух ТОЛЬКО между пунктами (margin-top у второго и далее), а не вокруг
   каждого: иначе первый и последний пункт отодвигают границы пузыря. */
.alg-msg-list-item { margin: 0; }
.alg-msg-list-item + .alg-msg-list-item { margin-top: 2px; }

/* АБЗАЦ внутри форматированного сообщения. markdown отдаёт «рыхлый»
   список как <li><p>…</p></li>, и без явного правила браузерный margin
   абзаца (1em сверху и снизу) разносил пункты на полторы строки.
   Ноль снаружи, воздух только между соседними абзацами. */
.alg-msg-paragraph { margin: 0; }
.alg-msg-paragraph + .alg-msg-paragraph { margin-top: 0.5em; }
.alg-msg-list-item > .alg-msg-paragraph { margin: 0; }

/* AUDIO — compact custom controls for voice notes and audio attachments.
   The range input itself remains the interaction surface: browser-native
   pointer, touch and keyboard seeking are retained. Its masked track creates
   waveform-like bars while --audio-progress fills bars already played. */
.alg-audio-player {
  width: min(340px, 100%);
  min-width: min(250px, 100%);
  box-sizing: border-box;
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr) 30px;
  align-items: center;
  gap: 8px;
  padding: 7px 8px;
  border: 1px solid ${color.borderSoft};
  border-radius: ${radius.md}px;
  background: ${color.sidebarBg};
}
.alg-audio-player audio { display: none; }
.alg-audio-toggle,
.alg-audio-download {
  width: 36px;
  height: 36px;
  box-sizing: border-box;
  border: 0;
  border-radius: 50%;
  display: inline-grid;
  place-items: center;
  font: inherit;
  font-size: 15px;
  line-height: 1;
  text-decoration: none;
  cursor: pointer;
  touch-action: manipulation;
}
.alg-audio-toggle {
  background: ${color.accent};
  color: ${color.textOnAccent};
}
.alg-audio-download {
  width: 30px;
  height: 30px;
  color: ${color.textDim};
  background: transparent;
  font-size: 21px;
}
.alg-audio-toggle:hover { filter: brightness(1.05); }
.alg-audio-download:hover { background: ${color.hover}; color: ${color.text}; }
.alg-audio-toggle:focus-visible,
.alg-audio-download:focus-visible,
.alg-audio-range:focus-visible {
  outline: 2px solid ${color.accent};
  outline-offset: 2px;
}
.alg-audio-main { min-width: 0; }
.alg-audio-range {
  --audio-progress: 0%;
  -webkit-appearance: none;
  appearance: none;
  display: block;
  width: 100%;
  height: 25px;
  margin: 0;
  border: 0;
  padding: 0;
  cursor: pointer;
  background: linear-gradient(
    to right,
    ${color.accent} 0 var(--audio-progress),
    ${color.border} var(--audio-progress) 100%
  );
  -webkit-mask: repeating-linear-gradient(90deg, currentColor 0 3px, transparent 3px 6px);
  mask: repeating-linear-gradient(90deg, currentColor 0 3px, transparent 3px 6px);
}
.alg-audio-range:disabled { cursor: default; opacity: 0.55; }
.alg-audio-range::-webkit-slider-runnable-track { height: 25px; background: transparent; }
.alg-audio-range::-moz-range-track { height: 25px; background: transparent; }
.alg-audio-range::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 10px;
  height: 25px;
  border: 0;
  background: transparent;
}
.alg-audio-range::-moz-range-thumb {
  width: 10px;
  height: 25px;
  border: 0;
  background: transparent;
}
.alg-audio-time {
  display: flex;
  justify-content: space-between;
  margin-top: 1px;
  color: ${color.textDim};
  font-size: ${font.tiny}px;
  line-height: 1.2;
  font-variant-numeric: tabular-nums;
}
@media (max-width: 640px) {
  .alg-audio-player {
    width: 100%;
    min-width: 0;
    grid-template-columns: 38px minmax(112px, 1fr) 28px;
    gap: 6px;
    padding-inline: 6px;
  }
  .alg-audio-toggle { width: 36px; height: 36px; }
  .alg-audio-download { width: 28px; height: 32px; }
}

/* Непрочитанные: мягкий фон и тонкая акцентная граница без тяжёлой тени. */
.alg-unread-message {
  background: ${color.accentSoft};
  border-left: 2px solid ${color.accent};
  border-radius: ${radius.sm}px;
}
`;
